export interface ImageConstraints {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: string;
}

export interface ImageValidationResult {
  valid: boolean;
  width?: number;
  height?: number;
  error?: string;
}

function parseAspectRatio(ratio?: string): number | null {
  if (!ratio) return null;
  const [w, h] = ratio.split(':').map(Number);
  if (!w || !h) return null;
  return w / h;
}

export function loadImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file'));
    };
    img.src = url;
  });
}

export function validateImageDimensions(
  width: number,
  height: number,
  constraints: ImageConstraints,
): ImageValidationResult {
  const { minWidth, minHeight, maxWidth, maxHeight, aspectRatio } = constraints;

  if (minWidth && width < minWidth) {
    return { valid: false, width, height, error: `Image width must be at least ${minWidth}px (got ${width}px)` };
  }
  if (minHeight && height < minHeight) {
    return { valid: false, width, height, error: `Image height must be at least ${minHeight}px (got ${height}px)` };
  }
  if (maxWidth && width > maxWidth) {
    return { valid: false, width, height, error: `Image width must be at most ${maxWidth}px (got ${width}px)` };
  }
  if (maxHeight && height > maxHeight) {
    return { valid: false, width, height, error: `Image height must be at most ${maxHeight}px (got ${height}px)` };
  }

  const targetRatio = parseAspectRatio(aspectRatio);
  if (targetRatio) {
    const actual = width / height;
    const tolerance = 0.08;
    if (Math.abs(actual - targetRatio) > tolerance) {
      return {
        valid: false,
        width,
        height,
        error: `Image aspect ratio should be ${aspectRatio} (got ${width}×${height})`,
      };
    }
  }

  return { valid: true, width, height };
}

export async function validateImageFile(
  file: File,
  constraints: ImageConstraints,
): Promise<ImageValidationResult> {
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image (PNG, JPEG, or WebP)' };
  }
  if (file.type === 'image/svg+xml' && (constraints.minWidth || constraints.minHeight)) {
    return { valid: true };
  }
  try {
    const { width, height } = await loadImageDimensions(file);
    return validateImageDimensions(width, height, constraints);
  } catch {
    return { valid: false, error: 'Could not read image dimensions' };
  }
}
